import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { admAPI } from "../../lib/admApi";
import { PageHeader, Loading, StatusPill, Section, AIInsightCard, EmptyState } from "../../components/Common";
import {
  ArrowLeft, Sparkles, Mail, CalendarPlus, Trophy, GraduationCap, FileText,
  Shield, CheckCircle2, XCircle, Clock, User, Users, MapPin, HeartPulse,
} from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  "application_draft", "application_submitted", "under_review",
  "assessment_scheduled", "interview_scheduled", "review_complete",
  "offer_issued", "waiting_list", "accepted", "enrolled",
  "rejected", "withdrawn",
];

const AI_KINDS = [
  { id: "summary", label: "Executive summary", call: "aiSummary" },
  { id: "missing", label: "Missing docs", call: "aiMissing" },
  { id: "recommend", label: "Workflow recommendations", call: "aiRecommend" },
  { id: "interview", label: "Interview kit", call: "aiInterviewKit" },
  { id: "essay", label: "Essay review", call: "aiEssay" },
  { id: "scholarship", label: "Scholarship insight", call: "aiScholarship" },
];

const COMM_KINDS = [
  { id: "acknowledgement", label: "Acknowledgement" },
  { id: "request_docs", label: "Request documents" },
  { id: "interview_invite", label: "Interview invitation" },
  { id: "assessment_invite", label: "Assessment invitation" },
  { id: "offer_letter", label: "Offer letter" },
  { id: "waiting_list", label: "Waiting list notice" },
  { id: "acceptance_confirm", label: "Acceptance confirmation" },
  { id: "reminder", label: "Reminder" },
];

export default function ApplicantDetail() {
  const { school, id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [interviewForm, setInterviewForm] = useState({ scheduled_at: "", type: "admissions", virtual: false, location: "Main Campus" });
  const [assessmentForm, setAssessmentForm] = useState({ scheduled_at: "", type: "academic", subject: "Mathematics" });
  const [offerForm, setOfferForm] = useState({ offer_type: "unconditional", scholarship_pct: 0, boarding_house: "" });
  const [draftPreview, setDraftPreview] = useState(null);

  const load = () => admAPI.getApplicant(id).then(({ data }) => setData(data)).catch(() => setData({ notFound: true }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <Loading />;
  if (data.notFound) return <EmptyState title="Applicant not found" body="It may have been removed." />;

  const a = data.applicant;
  const parent = (a.parents || [])[0] || {};

  const runAI = async (kind) => {
    const cfg = AI_KINDS.find(k => k.id === kind);
    setBusy(kind);
    try {
      const { data: res } = await admAPI[cfg.call](id);
      toast.success(cfg.label + " generated");
      load();
      return res;
    } catch { toast.error("AI unavailable"); return null; }
    finally { setBusy(null); }
  };

  const draftComm = async (kind) => {
    setBusy("comm-" + kind);
    try {
      const { data: res } = await admAPI.aiDraftComm(id, kind);
      setDraftPreview(res);
      toast.success("Draft ready for review");
      load();
    } catch { toast.error("Draft unavailable"); }
    finally { setBusy(null); }
  };

  const sendMessage = async () => {
    if (!draftPreview) return;
    try {
      // Extract subject line from AI body (first line after "Subject:")
      const body = draftPreview.body || "";
      const m = body.match(/Subject:\s*([^\n]+)/i);
      const subj = m ? m[1].trim() : (draftPreview.title || "Admissions message");
      await admAPI.sendMessage(id, { subject: subj, body, ai_drafted: true, channel: "portal" });
      toast.success("Message sent to applicant portal");
      setDraftPreview(null);
      load();
    } catch { toast.error("Send failed"); }
  };

  const scheduleInterview = async () => {
    if (!interviewForm.scheduled_at) return toast.error("Select date/time");
    try { await admAPI.createInterview(id, interviewForm); toast.success("Interview scheduled"); load(); }
    catch { toast.error("Failed"); }
  };
  const scheduleAssessment = async () => {
    if (!assessmentForm.scheduled_at) return toast.error("Select date/time");
    try { await admAPI.createAssessment(id, assessmentForm); toast.success("Assessment scheduled"); load(); }
    catch { toast.error("Failed"); }
  };
  const issueOffer = async () => {
    try {
      await admAPI.createOffer(id, offerForm);
      toast.success("Offer issued");
      load();
    } catch { toast.error("Failed"); }
  };
  const changeStage = async (stage) => {
    try { await admAPI.changeStage(id, stage); toast.success("Stage updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const enrol = async () => {
    if (!confirm(`Enrol ${a.first_name} ${a.last_name}? This creates a full student account, parent login, class allocation and initial invoice.`)) return;
    try {
      const { data: res } = await admAPI.enrol(id);
      if (res.already_enrolled) toast.info("Already enrolled");
      else toast.success(`Enrolled — student ${res.student?.admission_no || res.student_id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrolment failed"); }
  };

  return (
    <div data-testid={`applicant-${id}`}>
      <PageHeader
        kicker={`Admissions360™ · ${a.application_no}`}
        title={`${a.first_name} ${a.last_name}`}
        subtitle={`${a.year_group_applied} · ${a.nationality} · Entry ${a.entry_year}`}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => nav(`/education360/${school}/admissions/pipeline`)} className="hl-button-secondary text-xs">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> Pipeline
            </button>
            <StatusPill status={a.stage} />
          </div>
        }
      />

      {/* Header status bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="header-stats">
        <StatBox label="Priority" value={a.priority} sub={`Score ${a.priority_score || 0}`} />
        <StatBox label="Boarding" value={a.boarding_requested ? "Requested" : "Day"} />
        <StatBox label="Scholarship" value={a.scholarship_requested ? (a.scholarship_category || "Yes") : "No"} />
        <StatBox label="Completeness" value={`${data.completeness.score}%`} sub={data.completeness.complete ? "Complete" : `${data.completeness.missing.length} missing`} />
        <StatBox label="Stage" value={a.stage.replace(/_/g, " ")} />
      </div>

      {/* Stage transition */}
      <div className="hl-card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="stage-controls">
        <span className="text-xs uppercase tracking-widest text-slate-500">Move to</span>
        {STAGES.filter(s => s !== a.stage).map(s => (
          <button
            key={s}
            onClick={() => changeStage(s)}
            className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            data-testid={`stage-btn-${s}`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
        {(a.stage === "accepted") && (
          <button
            onClick={enrol}
            className="ml-auto hl-button-primary text-xs"
            data-testid="btn-enrol"
          >
            <GraduationCap className="w-3 h-3 inline mr-1" />Enrol as student
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Applicant profile */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="Applicant profile">
            <div className="hl-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-testid="profile-panel">
              <ProfileItem icon={User} label="Full name" value={`${a.first_name} ${a.middle_name || ""} ${a.last_name}`} />
              <ProfileItem icon={CalendarPlus} label="Date of birth" value={a.date_of_birth} />
              <ProfileItem icon={User} label="Gender" value={a.gender} />
              <ProfileItem icon={MapPin} label="Nationality" value={a.nationality} />
              <ProfileItem icon={GraduationCap} label="Year applied" value={a.year_group_applied} />
              <ProfileItem icon={FileText} label="Entry" value={`${a.entry_year} · ${a.entry_term}`} />
              <ProfileItem icon={Users} label="Languages" value={(a.languages || []).join(", ") || "—"} />
              <ProfileItem icon={HeartPulse} label="Medical" value={a.medical_conditions || a.allergies || "Nil"} />
            </div>
          </Section>

          <Section title="Family & references">
            <div className="hl-card p-5 space-y-3 text-sm">
              <div className="font-semibold text-slate-700">Parents / Guardians</div>
              {(a.parents || []).map((p, i) => (
                <div key={i} className="border-l-2 border-slate-200 pl-3 text-slate-700">
                  {p.first_name} {p.last_name} — {p.relationship} · <span className="mono">{p.email}</span> · {p.phone}
                </div>
              ))}
              {(a.parents || []).length === 0 && <div className="text-slate-500 text-xs">No parents on file.</div>}

              <div className="font-semibold text-slate-700 mt-3">References</div>
              {(a.references || []).map((r, i) => (
                <div key={i} className="border-l-2 border-slate-200 pl-3 text-slate-700">
                  {r.name} — {r.position}, {r.school} · <span className="mono">{r.email}</span>
                </div>
              ))}
              {(a.references || []).length === 0 && <div className="text-slate-500 text-xs">No references submitted.</div>}
            </div>
          </Section>

          <Section title="Academic history">
            <div className="hl-card p-5 text-sm">
              {(a.previous_schools || []).map((s, i) => (
                <div key={i} className="border-b border-slate-100 py-2">
                  <b>{s.name}</b> ({s.country}) — {s.curriculum} · {s.from_year}–{s.to_year}
                </div>
              ))}
              {(a.academic_history || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-slate-500 text-xs uppercase tracking-widest mb-1">Recent grades</div>
                  <div className="flex flex-wrap gap-1">
                    {a.academic_history.map((g, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 bg-slate-100 rounded">
                        {g.subject}: <b>{g.grade}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>

          {a.essay && (
            <Section title="Personal statement">
              <div className="hl-card p-5 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="essay">
                {a.essay}
              </div>
            </Section>
          )}

          <Section title="Documents">
            <div className="hl-card p-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {(a.documents || []).map((d, i) => (
                <div key={i} className="border border-slate-200 rounded p-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-700 truncate">{d.name}</div>
                    <div className="text-slate-500 uppercase text-[10px]">{d.type}</div>
                  </div>
                  {d.verified ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />}
                </div>
              ))}
              {(a.documents || []).length === 0 && <div className="col-span-full text-slate-500">No documents uploaded.</div>}
            </div>
            {!data.completeness.complete && (
              <div className="text-xs text-amber-700 mt-2">
                Missing: {data.completeness.missing.join(", ")}
              </div>
            )}
          </Section>

          <Section title="Interviews">
            <div className="hl-card p-5" data-testid="interviews-panel">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4 items-end text-sm">
                <input type="datetime-local" data-testid="int-datetime" className="border border-slate-200 rounded px-2 py-1" value={interviewForm.scheduled_at} onChange={e => setInterviewForm({ ...interviewForm, scheduled_at: e.target.value })} />
                <select value={interviewForm.type} onChange={e => setInterviewForm({ ...interviewForm, type: e.target.value })} className="border border-slate-200 rounded px-2 py-1">
                  {["admissions", "principal", "subject", "boarding"].map(t => <option key={t}>{t}</option>)}
                </select>
                <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={interviewForm.virtual} onChange={e => setInterviewForm({ ...interviewForm, virtual: e.target.checked })} /> Virtual</label>
                <button onClick={scheduleInterview} className="hl-button-primary text-xs" data-testid="btn-schedule-interview">Schedule</button>
              </div>
              {(data.interviews || []).map(iv => (
                <div key={iv.id} className="border-b border-slate-100 py-2 text-sm flex justify-between">
                  <div>
                    <b>{iv.type}</b> · {iv.scheduled_at?.replace("T", " ").slice(0, 16)} · {iv.virtual ? "Virtual" : iv.location}
                    {iv.notes && <div className="text-xs text-slate-500 mt-1">{iv.notes}</div>}
                  </div>
                  <StatusPill status={iv.status} />
                </div>
              ))}
              {(data.interviews || []).length === 0 && <div className="text-slate-500 text-xs">No interviews yet.</div>}
            </div>
          </Section>

          <Section title="Assessments">
            <div className="hl-card p-5" data-testid="assessments-panel">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4 items-end text-sm">
                <input type="datetime-local" data-testid="as-datetime" className="border border-slate-200 rounded px-2 py-1" value={assessmentForm.scheduled_at} onChange={e => setAssessmentForm({ ...assessmentForm, scheduled_at: e.target.value })} />
                <select value={assessmentForm.type} onChange={e => setAssessmentForm({ ...assessmentForm, type: e.target.value })} className="border border-slate-200 rounded px-2 py-1">
                  {["academic", "entrance", "practical", "portfolio", "sports", "music"].map(t => <option key={t}>{t}</option>)}
                </select>
                <input value={assessmentForm.subject} onChange={e => setAssessmentForm({ ...assessmentForm, subject: e.target.value })} placeholder="Subject" className="border border-slate-200 rounded px-2 py-1" />
                <button onClick={scheduleAssessment} className="hl-button-primary text-xs" data-testid="btn-schedule-assessment">Schedule</button>
              </div>
              {(data.assessments || []).map(as => (
                <div key={as.id} className="border-b border-slate-100 py-2 text-sm flex justify-between">
                  <div>
                    <b>{as.type}</b> {as.subject ? `· ${as.subject}` : ""} · {as.scheduled_at?.replace("T", " ").slice(0, 16)}
                    {as.score && <span className="ml-2 text-xs text-emerald-700 font-semibold">Score {as.score}/{as.max_score}</span>}
                  </div>
                  <StatusPill status={as.status} />
                </div>
              ))}
              {(data.assessments || []).length === 0 && <div className="text-slate-500 text-xs">No assessments yet.</div>}
            </div>
          </Section>

          <Section title="Offers">
            <div className="hl-card p-5" data-testid="offers-panel">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4 items-end text-sm">
                <select value={offerForm.offer_type} onChange={e => setOfferForm({ ...offerForm, offer_type: e.target.value })} className="border border-slate-200 rounded px-2 py-1">
                  {["unconditional", "conditional", "scholarship", "boarding"].map(t => <option key={t}>{t}</option>)}
                </select>
                <input type="number" placeholder="Scholarship %" value={offerForm.scholarship_pct} onChange={e => setOfferForm({ ...offerForm, scholarship_pct: Number(e.target.value) })} className="border border-slate-200 rounded px-2 py-1" />
                <select value={offerForm.boarding_house} onChange={e => setOfferForm({ ...offerForm, boarding_house: e.target.value })} className="border border-slate-200 rounded px-2 py-1">
                  <option value="">No boarding house</option>
                  {["Cardigan", "Windsor", "Blenheim", "Sandringham"].map(h => <option key={h}>{h}</option>)}
                </select>
                <button onClick={issueOffer} className="hl-button-primary text-xs md:col-span-2" data-testid="btn-issue-offer">
                  <Trophy className="w-3 h-3 inline mr-1" />Issue offer
                </button>
              </div>
              {(data.offers || []).map(o => (
                <div key={o.id} className="border-b border-slate-100 py-2 text-sm">
                  <div className="flex justify-between">
                    <div><b>{o.offer_type}</b> — {o.year_group} · deadline {o.acceptance_deadline}</div>
                    <StatusPill status={o.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Annual total: ${(o.fee_summary?.annual_total || 0).toLocaleString()}
                    {o.scholarship_pct ? ` · Scholarship ${o.scholarship_pct}%` : ""}
                    {o.boarding_house ? ` · Boarding: ${o.boarding_house}` : ""}
                  </div>
                </div>
              ))}
              {(data.offers || []).length === 0 && <div className="text-slate-500 text-xs">No offers yet.</div>}
            </div>
          </Section>
        </div>

        {/* Right column — AI + comms + audit */}
        <div className="space-y-6">
          <Section title="AI decision support" subtitle="Advisory only. Final calls stay with the school.">
            <div className="hl-card p-5 space-y-2" data-testid="ai-panel">
              {AI_KINDS.map(k => (
                <button
                  key={k.id}
                  onClick={() => runAI(k.id)}
                  disabled={busy === k.id}
                  className="w-full flex items-center justify-between text-left border border-slate-200 rounded px-3 py-2 text-sm hover:border-[color:var(--hl-orange)] disabled:opacity-50"
                  data-testid={`ai-${k.id}`}
                >
                  <span><Sparkles className="w-3.5 h-3.5 inline mr-2 text-[color:var(--hl-orange)]" />{k.label}</span>
                  {busy === k.id ? <Clock className="w-3.5 h-3.5 animate-spin text-slate-400" /> : <span className="text-xs text-slate-400">Generate</span>}
                </button>
              ))}
            </div>
          </Section>

          {(data.ai_briefings || []).length > 0 && (
            <Section title="Recent AI briefings">
              <div className="space-y-3" data-testid="briefings-list">
                {(data.ai_briefings || []).slice(0, 4).map(b => (
                  <AIInsightCard key={b.id} testId={`brief-${b.kind}`} title={b.title} body={b.body} severity="advisory" />
                ))}
              </div>
            </Section>
          )}

          <Section title="Communication drafts" subtitle="AI drafts are queued for staff approval.">
            <div className="hl-card p-5 space-y-2" data-testid="comm-panel">
              {COMM_KINDS.map(k => (
                <button
                  key={k.id}
                  onClick={() => draftComm(k.id)}
                  disabled={busy === "comm-" + k.id}
                  className="w-full flex items-center justify-between text-left border border-slate-200 rounded px-3 py-2 text-sm hover:border-[color:var(--hl-orange)] disabled:opacity-50"
                  data-testid={`comm-${k.id}`}
                >
                  <span><Mail className="w-3.5 h-3.5 inline mr-2 text-slate-500" />{k.label}</span>
                  {busy === "comm-" + k.id ? <Clock className="w-3.5 h-3.5 animate-spin" /> : <span className="text-xs text-slate-400">Draft</span>}
                </button>
              ))}
            </div>
          </Section>

          {draftPreview && (
            <div className="hl-card p-5 border-2 border-[color:var(--hl-orange)]" data-testid="draft-preview">
              <div className="flex items-center justify-between mb-2">
                <b className="text-sm">Draft ready for review</b>
                <button onClick={() => setDraftPreview(null)} className="text-xs text-slate-500">Discard</button>
              </div>
              <div className="text-xs text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto">{draftPreview.body}</div>
              <button onClick={sendMessage} className="hl-button-primary text-xs mt-3" data-testid="btn-approve-send">
                Approve & send to portal
              </button>
            </div>
          )}

          <Section title="Audit trail">
            <div className="hl-card p-4 max-h-64 overflow-y-auto text-xs space-y-2" data-testid="audit-panel">
              {(data.audit || []).map((e, i) => (
                <div key={i} className="border-l-2 border-slate-200 pl-2">
                  <div className="text-slate-500">{(e.created_at || "").replace("T", " ").slice(0, 16)}</div>
                  <div className="text-slate-700"><b>{e.action}</b> — {e.detail}</div>
                  <div className="text-slate-400">{e.actor_email} ({e.actor_role})</div>
                </div>
              ))}
              {(data.audit || []).length === 0 && <div className="text-slate-500">No activity yet.</div>}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, sub }) {
  return (
    <div className="hl-card p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800 capitalize">{value || "—"}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ProfileItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-slate-700">{value || "—"}</div>
      </div>
    </div>
  );
}
